<script setup>
import { computed, ref } from "vue"

// The cart is server state: every mutation posts to /cart and the response is
// the new truth. Nothing about pricing is computed in the browser, so a user
// poking at the console can change what they *see* but never what they pay.
const props = defineProps({
  cart: { type: Object, required: true },
  checkoutPath: { type: String, required: true },
  cartPath: { type: String, required: true },
  configured: { type: Boolean, default: false },
})

const cart = ref(props.cart)
const busy = ref(false)
const error = ref(null)

const csrfToken = () =>
  document.querySelector('meta[name="csrf-token"]')?.content ?? ""

const money = (cents) =>
  (cents / 100).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
  })

const canCheckout = computed(
  () => props.configured && !cart.value.empty && !busy.value
)

async function send(url, method, body) {
  busy.value = true
  error.value = null

  try {
    const response = await fetch(url, {
      method,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": csrfToken(),
      },
      body: body ? JSON.stringify(body) : undefined,
    })

    if (!response.ok) throw new Error(`${response.status}`)
    cart.value = await response.json()
  } catch {
    // Deliberately vague: the useful recovery is always the same, and a stack
    // trace in the sidebar helps nobody's grandmother.
    error.value = "Something went wrong. Please refresh and try again."
  } finally {
    busy.value = false
  }
}

const setQuantity = (line, quantity) =>
  send(`${props.cartPath}?id=${encodeURIComponent(line.line_id)}`, "PATCH", {
    quantity,
  })

const remove = (line) =>
  send(`${props.cartPath}?id=${encodeURIComponent(line.line_id)}`, "DELETE")
</script>

<template>
  <div class="border-2 border-cream-950 bg-cream-100">
    <div class="border-b-2 border-cream-950 px-6 py-4">
      <h2 class="text-[12px] font-bold tracking-[0.2em] text-cream-950">
        YOUR CART
        <span v-if="cart.item_count" class="text-cream-600">
          · {{ cart.item_count }}
        </span>
      </h2>
    </div>

    <p v-if="cart.empty" class="px-6 py-8 text-[15px] leading-[1.5] text-cream-600">
      Nothing added yet. Dues cover the meals and the hall; shirts are ordered
      in one run after the cutoff.
    </p>

    <ul v-else class="flex flex-col">
      <li
        v-for="line in cart.lines"
        :key="line.line_id"
        class="flex flex-col gap-3 border-b-2 border-cream-300 px-6 py-5"
      >
        <div class="flex items-baseline justify-between gap-4">
          <span class="text-[17px] font-bold leading-tight tracking-[-0.01em]">
            {{ line.label }}
          </span>
          <span class="shrink-0 text-[17px] font-bold text-reunion-600">
            {{ money(line.amount_cents) }}
          </span>
        </div>

        <div class="flex items-center justify-between gap-4">
          <div class="flex items-center border-2 border-cream-950">
            <button
              type="button"
              class="h-9 w-9 text-[18px] font-bold leading-none transition-colors hover:bg-cream-300 disabled:opacity-40"
              :disabled="busy"
              :aria-label="`Remove one ${line.label}`"
              @click="setQuantity(line, line.quantity - 1)"
            >
              −
            </button>
            <span class="w-10 text-center text-[15px] font-bold tabular-nums">
              {{ line.quantity }}
            </span>
            <button
              type="button"
              class="h-9 w-9 text-[18px] font-bold leading-none transition-colors hover:bg-cream-300 disabled:opacity-40"
              :disabled="busy || line.quantity >= line.max_quantity"
              :aria-label="`Add one ${line.label}`"
              @click="setQuantity(line, line.quantity + 1)"
            >
              +
            </button>
          </div>

          <button
            type="button"
            class="text-[11px] font-bold tracking-[0.14em] text-cream-600 underline underline-offset-4 transition-colors hover:text-reunion-600"
            :disabled="busy"
            @click="remove(line)"
          >
            REMOVE
          </button>
        </div>
      </li>
    </ul>

    <div class="flex flex-col gap-4 px-6 py-5">
      <div class="flex items-baseline justify-between border-t-2 border-cream-950 pt-4">
        <span class="text-[11px] font-bold tracking-[0.18em] text-cream-600">TOTAL</span>
        <span class="text-[28px] font-extrabold tracking-[-0.02em]">
          {{ money(cart.subtotal_cents) }}
        </span>
      </div>

      <p v-if="error" class="text-[13px] font-semibold text-reunion-700">{{ error }}</p>

      <form :action="checkoutPath" method="post">
        <input type="hidden" name="authenticity_token" :value="csrfToken()" />
        <!-- The total the buyer is actually looking at. The server re-reads
             prices from Stripe and refuses the charge if this no longer
             matches, so a dashboard price change mid-session cannot surprise
             anyone. -->
        <input
          type="hidden"
          name="expected_total_cents"
          :value="cart.subtotal_cents"
        />
        <button
          type="submit"
          class="w-full bg-reunion-600 px-6 py-4 text-[13px] font-bold tracking-[0.12em] text-cream-50 transition-colors hover:bg-reunion-700 disabled:cursor-not-allowed disabled:bg-cream-400"
          :disabled="!canCheckout"
        >
          {{ configured ? "PAY WITH CARD" : "PAYMENTS NOT OPEN YET" }}
        </button>
      </form>

      <p class="text-[12px] leading-[1.45] text-cream-600">
        Payment is handled by Stripe on their own secure page. Card details
        never touch this site.
      </p>
    </div>
  </div>
</template>
